import { useEffect, useState } from "react";
import TimeTracker from "./components/TimeTracker";
import {
  initTimeTrackerState,
  useTimeTrackerStore,
} from "./store/timeTrackerStore";
import WeeklyClientSummary from "./components/WeeklyClientSummary";
import AnalyticsModal from "./components/AnalyticsModal";
import LemonPiLogo from "./components/LemonPiLogo";
import Landing from "./components/Landing";
import { useUrlParam } from "./utils/useUrlState";
import { modalCodec } from "./utils/modalParam";
import { Badge, Card, ToastProvider } from "@lazar/ui";

// Remembers that a visitor has dismissed the landing.
const LANDING_DISMISS_KEY = "lemonpi-landing-dismissed";

function landingDismissed(): boolean {
  try {
    return localStorage.getItem(LANDING_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberLandingDismissed(): void {
  try {
    localStorage.setItem(LANDING_DISMISS_KEY, "1");
  } catch {
    // Best-effort only. The tool works regardless.
  }
}

function App() {
  const [modal, setModal] = useUrlParam("modal", modalCodec);

  // Newcomers (no tracked entries yet) get the pitch; everyone else starts
  // collapsed. The store's persist middleware hydrates synchronously, so the
  // entry count is already accurate on first render.
  const [landingOpen, setLandingOpen] = useState(() => {
    const hasData =
      Object.keys(useTimeTrackerStore.getState().timeEntries).length > 0;
    return !landingDismissed() && !hasData;
  });

  const closeLanding = () => {
    rememberLandingDismissed();
    setLandingOpen(false);
  };

  const startTracking = () => {
    closeLanding();
    document
      .getElementById("tracker")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    initTimeTrackerState();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "KeyA") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      e.preventDefault();
      setModal(modal === "analytics" ? "none" : "analytics");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modal, setModal]);

  return (
    <ToastProvider>
      <div className="flex min-h-full flex-col bg-lemon-50 dark:bg-ink text-ink dark:text-cream transition-colors duration-200">
        <header className="mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between border-b-[3px] border-ink dark:border-cream pb-4">
            <div className="flex items-center gap-3">
              <LemonPiLogo className="h-12 w-12" />
              <div className="flex flex-col">
                <h1 className="text-3xl font-black tracking-tight leading-none">
                  Lemon Pi
                </h1>
                <p className="text-xs font-medium opacity-70 mt-1">
                  time, sliced
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  landingOpen ? closeLanding() : setLandingOpen(true)
                }
                aria-expanded={landingOpen}
                className={`brut-badge cursor-pointer ${
                  landingOpen
                    ? "bg-lemon-200 text-ink"
                    : "bg-paper text-ink dark:bg-charcoal dark:text-cream"
                }`}
              >
                what is Lemon Pi?
              </button>
              <Badge className="bg-lemon-500 text-ink hidden sm:inline-flex">
                local-first
              </Badge>
            </div>
          </div>
        </header>

        {landingOpen && (
          <Landing onStart={startTracking} onClose={closeLanding} />
        )}

        <div className="mx-auto flex w-full max-w-7xl items-start gap-x-8 px-4 py-8 sm:px-6 lg:px-8">
          <main id="tracker" className="flex-1 min-w-0">
            <Card className="p-6 sm:p-8">
              <TimeTracker />
            </Card>
          </main>

          <aside className="sticky top-8 hidden w-96 shrink-0 xl:block">
            <WeeklyClientSummary />
          </aside>
        </div>

        {/* Mounted at the shell level, not inside the xl-only sidebar, so the
            full-screen overlay paints at every width. A fixed-position modal is
            still clipped to nothing when an ancestor is display:none, so it must
            not live under the responsive-hidden aside. The A shortcut and the
            settings modal already open at any width — this keeps analytics
            consistent with them. */}
        <AnalyticsModal
          isOpen={modal === "analytics"}
          onClose={() => setModal("none")}
        />

        <a
          href="https://alexlazar.dev"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-4 right-4 brut-badge bg-ink text-cream dark:bg-cream dark:text-ink"
        >
          built by Alex
        </a>
      </div>
    </ToastProvider>
  );
}

export default App;
